import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Wrench, Car, Printer, Pencil, Trash2, Plus, BookOpen, ChevronRight, QrCode } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import { BARVA_BG, BARVA_BORDER, TYPY, api, qrOdkaz, barva as barvaZnalosti } from './znalosti';

/**
 * Formulář kusu zařízení (Fritéza 2) — jen admin.
 */
export function ZarizeniEditor({ isDark, puvodni = null, typyStroju = [], onClose, onUlozeno }) {
    const akcent = barvaZnalosti(isDark);
    const cara = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const muted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const input = {
        width: '100%', minHeight: 44, borderRadius: 10, padding: '0 12px', outline: 'none', fontSize: 14,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${cara}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
    const stitek = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: muted, margin: '14px 0 6px' };
    const [f, setF] = useState({
        kod: puvodni?.kod || '', nazev: puvodni?.nazev || '', stroj_id: puvodni?.stroj_id || '',
        umisteni: puvodni?.umisteni || '', vyrobce: puvodni?.vyrobce || '', vyrobni_cislo: puvodni?.vyrobni_cislo || '',
        aktivni: puvodni ? !!puvodni.aktivni : true, poznamka: puvodni?.poznamka || '',
    });
    const [chyba, setChyba] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    const zmen = (k, v) => setF(s => ({ ...s, [k]: v }));

    const ulozit = async () => {
        setUkladam(true); setChyba(null);
        try {
            const z = await api(puvodni ? `/api/znalosti/zarizeni/${puvodni.id}` : '/api/znalosti/zarizeni', {
                method: puvodni ? 'PUT' : 'POST',
                body: { ...f, stroj_id: f.stroj_id || null },
            });
            onUlozeno(z);
        } catch (e) {
            setChyba(e.message);
            setUkladam(false);
        }
    };

    const paticka = (
        <div>
            {chyba && <p style={{ margin: '0 0 8px', fontSize: 13, color: '#EF4444' }}>{chyba}</p>}
            <button type="button" onClick={ulozit} disabled={ukladam || !f.kod.trim() || !f.nazev.trim()}
                style={{ height: 48, width: '100%', borderRadius: 12, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', background: akcent, color: '#fff', opacity: ukladam || !f.kod.trim() || !f.nazev.trim() ? 0.5 : 1 }}>
                {ukladam ? 'Ukládám…' : 'Uložit'}
            </button>
        </div>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={puvodni ? 'Upravit zařízení' : 'Nové zařízení'} ikona={Wrench} barva={akcent} paticka={paticka}>
            <div style={{ padding: '0 2px 8px' }}>
                <div className="flex" style={{ gap: 10 }}>
                    <div style={{ flex: '0 0 120px' }}>
                        <label style={{ ...stitek, marginTop: 4 }}>Kód</label>
                        <input style={input} value={f.kod} onChange={e => zmen('kod', e.target.value.toUpperCase())} placeholder="FRI-02" maxLength={20} />
                    </div>
                    <div className="flex-1">
                        <label style={{ ...stitek, marginTop: 4 }}>Název</label>
                        <input style={input} value={f.nazev} onChange={e => zmen('nazev', e.target.value)} placeholder="Fritéza 2" maxLength={120} />
                    </div>
                </div>
                <label style={stitek}>Typ stroje</label>
                <select style={input} value={f.stroj_id} onChange={e => zmen('stroj_id', e.target.value ? Number(e.target.value) : '')}>
                    <option value="">— bez typu —</option>
                    {typyStroju.map(s => <option key={s.id} value={s.id}>{s.nazev}</option>)}
                </select>
                <p style={{ fontSize: 11.5, color: muted, margin: '6px 0 0' }}>Kus ukáže i jednobodovky navázané na svůj typ stroje.</p>
                <label style={stitek}>Umístění</label>
                <input style={input} value={f.umisteni} onChange={e => zmen('umisteni', e.target.value)} placeholder="Kuchyně, linka vlevo" maxLength={120} />
                <div className="flex" style={{ gap: 10 }}>
                    <div className="flex-1">
                        <label style={stitek}>Výrobce</label>
                        <input style={input} value={f.vyrobce} onChange={e => zmen('vyrobce', e.target.value)} maxLength={120} />
                    </div>
                    <div className="flex-1">
                        <label style={stitek}>Výrobní číslo</label>
                        <input style={input} value={f.vyrobni_cislo} onChange={e => zmen('vyrobni_cislo', e.target.value)} maxLength={80} />
                    </div>
                </div>
                <label style={stitek}>Poznámka</label>
                <textarea style={{ ...input, padding: 10, minHeight: 64, resize: 'vertical' }} value={f.poznamka} onChange={e => zmen('poznamka', e.target.value)} maxLength={2000} />
                <label className="flex items-center" style={{ gap: 8, marginTop: 12, fontSize: 13.5, color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={f.aktivni} onChange={e => zmen('aktivni', e.target.checked)} style={{ width: 18, height: 18, accentColor: akcent }} />
                    V provozu
                </label>
            </div>
        </ModalShell>
    );
}

/** Vytiskne štítek s QR kódem (A6 na šířku — vejde se na stroj). */
function tiskStitku(z, dataUrl) {
    const okno = window.open('', '_blank', 'width=600,height=500');
    if (!okno) return;
    const esc = (t) => String(t ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    okno.document.write(`<!doctype html><html lang="cs"><head><meta charset="utf-8"><title>Štítek ${esc(z.kod)}</title>
<style>@page{size:A6 landscape;margin:8mm}body{font-family:-apple-system,"Segoe UI",Roboto,sans-serif;margin:0}
.s{display:flex;gap:6mm;align-items:center;border:0.6mm solid #111;border-radius:4mm;padding:5mm}
img{width:42mm;height:42mm}.k{font-size:9pt;font-weight:700;letter-spacing:.06em;color:#6b6b7a}
.n{font-size:20pt;font-weight:800;margin:1mm 0 3mm}.p{font-size:10pt;line-height:1.35}.f{font-size:8pt;color:#6b6b7a;margin-top:3mm}</style>
</head><body><div class="s"><img src="${dataUrl}" alt=""><div><div class="k">ZNALOSTI · ${esc(z.kod)}</div>
<div class="n">${esc(z.nazev)}</div><div class="p">Naskenuj telefonem a otevři<br><b>návody a jednobodovky</b> k tomuto zařízení.</div>
<div class="f">FofrLama OS</div></div></div><script>window.onload=()=>{window.print()}</script></body></html>`);
    okno.document.close();
}

/**
 * Detail kusu zařízení nebo auta: QR štítek, jednobodovky, správa.
 * `objekt` = `{ druh: 'zarizeni' | 'vozidlo', ...řádek }`.
 */
export function ZarizeniDetail({ objekt, isDark, isMobile, smiSpravovat, smiPsatClanky = false, onZpet, onOtevritClanek, onUpravit, onSmazat, onNovyClanek, rezervaDole }) {
    const akcent = barvaZnalosti(isDark);
    const muted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const cara = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const [clanky, setClanky] = useState(null);
    const [qr, setQr] = useState(null);
    const jeAuto = objekt.druh === 'vozidlo';

    useEffect(() => {
        setClanky(null);
        api(`/api/znalosti/k-objektu?typ=${objekt.druh}&id=${objekt.id}`).then(d => setClanky(d.clanky || [])).catch(() => setClanky([]));
    }, [objekt.druh, objekt.id]);

    useEffect(() => {
        setQr(null);
        if (jeAuto || !objekt.qr_token) return;
        QRCode.toDataURL(qrOdkaz(objekt.qr_token), { margin: 1, width: 360, errorCorrectionLevel: 'M' }).then(setQr).catch(() => {});
    }, [jeAuto, objekt.qr_token]);

    const tlac = { height: isMobile ? 44 : 34, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: `1px solid ${cara}`, color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 6 };
    const radekUdaje = (popis, hodnota) => hodnota ? (
        <div className="flex justify-between" style={{ minHeight: 36, alignItems: 'center', borderBottom: `1px solid ${cara}`, fontSize: 13.5, gap: 12 }}>
            <span style={{ color: muted }}>{popis}</span><span style={{ color: 'var(--text-primary)', fontWeight: 600, textAlign: 'right' }}>{hodnota}</span>
        </div>
    ) : null;

    return (
        <div style={{ padding: isMobile ? '10px 16px 16px' : '18px 22px 22px', maxWidth: 720 }}>
            {isMobile && <ZpetTlacitko onClick={onZpet} popis="Zpět na zařízení" sklo isDark={isDark} style={{ marginBottom: 12 }} />}
            <div className="flex items-start" style={{ gap: 10 }}>
                <div className="flex-1 min-w-0">
                    <p className="flex items-center" style={{ gap: 6, margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: akcent }}>
                        {jeAuto ? <Car size={13} /> : <Wrench size={13} />} {jeAuto ? 'Auto' : 'Zařízení'}{!jeAuto && objekt.kod ? ` · ${objekt.kod}` : ''}
                    </p>
                    <h1 style={{ margin: '6px 0 4px', fontSize: isMobile ? 24 : 22, fontWeight: 750, letterSpacing: '-0.4px', color: 'var(--text-primary)' }}>
                        {jeAuto ? objekt.spz : objekt.nazev}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: muted }}>
                        {jeAuto ? objekt.model : [objekt.stroj, objekt.umisteni].filter(Boolean).join(' · ')}
                        {!jeAuto && !objekt.aktivni && <span style={{ color: '#EF4444', fontWeight: 700 }}> · mimo provoz</span>}
                    </p>
                </div>
                {smiSpravovat && !jeAuto && !isMobile && (
                    <div className="flex" style={{ gap: 6, flexShrink: 0 }}>
                        <button type="button" style={tlac} onClick={onUpravit}><Pencil size={13} /> Upravit</button>
                        <button type="button" style={{ ...tlac, color: '#EF4444' }} onClick={onSmazat} aria-label="Smazat"><Trash2 size={13} /></button>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between" style={{ margin: '22px 2px 10px' }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: muted }}>Jednobodovky a návody</p>
                {smiPsatClanky && (
                    <button type="button" onClick={onNovyClanek} className="inline-flex items-center"
                        style={{ gap: 4, height: 32, padding: '0 10px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: akcent, background: BARVA_BG, border: `1px solid ${BARVA_BORDER}` }}>
                        <Plus size={14} /> Nová
                    </button>
                )}
            </div>
            {clanky === null && <p style={{ fontSize: 13, color: muted }}>Načítám…</p>}
            {clanky?.length === 0 && (
                <p style={{ fontSize: 13.5, color: muted, padding: '14px 0' }}>
                    K {jeAuto ? 'autu' : 'zařízení'} zatím nic není.{smiPsatClanky ? ' Založ první jednobodovku — vazba se vyplní sama.' : ''}
                </p>
            )}
            {clanky?.map(c => (
                <button key={c.id} type="button" onClick={() => onOtevritClanek(c.id)} className="w-full text-left flex items-center"
                    style={{ gap: 12, minHeight: 60, padding: '10px 12px', marginBottom: 8, borderRadius: 12, cursor: 'pointer', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.025)', border: `1px solid ${cara}` }}>
                    <span className="grid place-items-center" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: BARVA_BG, color: akcent }}><BookOpen size={18} /></span>
                    <span className="flex-1 min-w-0">
                        <span className="block truncate" style={{ fontSize: 14.5, fontWeight: 650, color: 'var(--text-primary)' }}>{c.nazev}</span>
                        <span className="block truncate" style={{ fontSize: 12, color: muted, marginTop: 2 }}>
                            {TYPY[c.typ].zkratka}{c.pocet_kroku ? ` · ${c.pocet_kroku} kroků` : ''}{c.video ? ' · video' : ''}
                        </span>
                    </span>
                    <ChevronRight size={18} style={{ color: muted }} />
                </button>
            ))}

            {!jeAuto && (
                <>
                    <p style={{ margin: '22px 2px 10px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: muted }}>QR štítek</p>
                    <div className="flex items-center" style={{ gap: 16, padding: 14, borderRadius: 14, border: `1px solid ${cara}` }}>
                        <div style={{ width: 120, height: 120, borderRadius: 10, background: '#fff', padding: 6, flexShrink: 0, display: 'grid', placeItems: 'center' }}>
                            {qr ? <img src={qr} alt={`QR kód ${objekt.kod}`} style={{ width: '100%', height: '100%' }} /> : <QrCode size={40} color="#999" />}
                        </div>
                        <div className="min-w-0">
                            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-primary)', lineHeight: 1.45 }}>Nalep na stroj. Telefon po naskenování otevře jednobodovky tohoto kusu.</p>
                            {qr && (
                                <button type="button" onClick={() => tiskStitku(objekt, qr)} style={{ ...tlac, marginTop: 10 }}>
                                    <Printer size={14} /> Vytisknout štítek
                                </button>
                            )}
                        </div>
                    </div>
                    <div style={{ marginTop: 16 }}>
                        {radekUdaje('Výrobce', objekt.vyrobce)}
                        {radekUdaje('Výrobní číslo', objekt.vyrobni_cislo)}
                        {radekUdaje('Poznámka', objekt.poznamka)}
                    </div>
                </>
            )}

            {smiSpravovat && !jeAuto && isMobile && (
                <div className="flex" style={{ gap: 8, marginTop: 20 }}>
                    <button type="button" style={tlac} onClick={onUpravit}><Pencil size={14} /> Upravit</button>
                    <button type="button" style={{ ...tlac, color: '#EF4444' }} onClick={onSmazat}><Trash2 size={14} /> Smazat</button>
                </div>
            )}
            {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
        </div>
    );
}
